import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  SelectContainer,
  SelectLabel,
  SelectButton,
  SelectIcon,
  DropdownList,
  DropdownOption,
  ErrorMessage,
  SearchInput
} from './styled'
import { ChevronDown } from '../../../assets/icons'

const Select = ({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Seleccionar...',
  error,
  disabled = false,
  required = false,
  searchable = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef(null)
  const inputRef = useRef(null)

  const selectedOption = options.find(option => option.value === value)
  const displayText = selectedOption ? selectedOption.label : placeholder

  const filteredOptions = searchable && searchTerm
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
        if (searchable) {
          setSearchTerm('')
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, searchable])

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
        if (searchable) {
          setSearchTerm('')
        }
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, searchable])

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
      if (searchable && !isOpen) {
        setTimeout(() => inputRef.current.focus(), 0)
      }
    }
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
    if (!isOpen) {
      setIsOpen(true)
    }
  }

  const handleSelect = (option) => {
    const syntheticEvent = {
      target: {
        name: name,
        value: option.value
      }
    }
    onChange(syntheticEvent)
    setIsOpen(false)
    if (searchable) {
      setSearchTerm('')
    }
  }

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <SelectContainer>
        {label && (
          <SelectLabel htmlFor={name}>
            {label}
            {required && <span style={{ color: '#D32F2F' }}> *</span>}
          </SelectLabel>
        )}
        
        <div style={{ position: 'relative' }}>
          {searchable ? (
            <>
              <SearchInput
                ref={inputRef}
                type="text"
                id={name}
                value={searchTerm || (selectedOption ? selectedOption.label : '')}
                onChange={handleSearchChange}
                onFocus={() => setIsOpen(true)}
                placeholder={placeholder}
                disabled={disabled}
                $error={!!error}
                $isOpen={isOpen}
                {...props}
              />
              <SelectIcon $isOpen={isOpen} onClick={handleToggle}>
                <ChevronDown />
              </SelectIcon>
            </>
          ) : (
            <SelectButton
              type="button"
              id={name}
              onClick={handleToggle}
              disabled={disabled}
              $error={!!error}
              $isOpen={isOpen}
              $hasValue={!!value}
              {...props}
            >
              {displayText}
              <SelectIcon $isOpen={isOpen}>
                <ChevronDown />
              </SelectIcon>
            </SelectButton>
          )}

          {isOpen && (
            <DropdownList>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <DropdownOption
                    key={option.value}
                    onClick={() => handleSelect(option)}
                    $isSelected={option.value === value}
                  >
                    {option.label}
                  </DropdownOption>
                ))
              ) : (
                <DropdownOption style={{ cursor: 'default', color: '#999' }}>
                  No se encontraron resultados
                </DropdownOption>
              )}
            </DropdownList>
          )}
        </div>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SelectContainer>
    </div>
  )
}

Select.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  searchable: PropTypes.bool
}

export default Select